import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  User,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { CollegeAccount, CoachRecord, FirmAccount, normalizeAccount, normalizeCoach, normalizeFirm } from '../types';

export interface CollegeAdminInfo {
  accountId: string;
  account: CollegeAccount;
  isPrimaryAdmin: boolean;
}

export interface FirmAdminInfo {
  firmId: string;
  firm: FirmAccount;
  isPrimaryAdmin: boolean;
}

export interface CoachInfo {
  coachId: string;
  coach: CoachRecord;
}

interface AuthContextType {
  user: User | null;
  collegeAdmin: CollegeAdminInfo | null;
  firmAdmin: FirmAdminInfo | null;
  coach: CoachInfo | null;
  loading: boolean;
  accessDenied: boolean;
  pendingMagicLink: boolean;
  magicLinkInProgress: boolean;
  magicLinkError: string;
  signInWithGoogle: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const MAGIC_EMAIL_KEY = 'klarr_admin_magic_email';

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const ACTION_CODE_SETTINGS = {
  url: 'https://admin.klarr.ai',
  handleCodeInApp: true,
};

function hasMagicLinkParams(href: string): boolean {
  try {
    const params = new URL(href).searchParams;
    return params.get('mode') === 'signIn' && params.has('oobCode');
  } catch {
    return false;
  }
}

async function resolveUser(
  firebaseUser: User,
  setUser: (u: User | null) => void,
  setCollegeAdmin: (c: CollegeAdminInfo | null) => void,
  setFirmAdmin: (f: FirmAdminInfo | null) => void,
  setCoach: (c: CoachInfo | null) => void,
  setAccessDenied: (v: boolean) => void,
) {
  // 1. Klarr super admin — mutually exclusive with all portal roles
  const profileSnap = await getDoc(doc(db, 'profiles', firebaseUser.uid));
  if (profileSnap.exists() && profileSnap.data()?.isAdmin === true) {
    setUser(firebaseUser);
    setCollegeAdmin(null);
    setFirmAdmin(null);
    setCoach(null);
    setAccessDenied(false);
    return;
  }

  if (firebaseUser.email) {
    // 2+3. Firm admin and coach — resolved independently so both can be non-null.
    // Run all initial lookups in parallel: 2 firm queries + coach-by-uid.
    const [firmPrimaryResult, firmAdditionalResult, coachByUidResult] = await Promise.allSettled([
      getDocs(query(
        collection(db, 'firms'),
        where('contactEmail', '==', firebaseUser.email),
        where('active', '==', true),
      )),
      getDocs(query(
        collection(db, 'firms'),
        where('adminEmails', 'array-contains', firebaseUser.email),
        where('active', '==', true),
      )),
      getDocs(query(
        collection(db, 'coaches'),
        where('uid', '==', firebaseUser.uid),
        where('status', '==', 'active'),
      )),
    ]);

    // Resolve firm admin
    let resolvedFirmAdmin: FirmAdminInfo | null = null;
    const firmPrimary = firmPrimaryResult.status === 'fulfilled' ? firmPrimaryResult.value : null;
    const firmAdditional = firmAdditionalResult.status === 'fulfilled' ? firmAdditionalResult.value : null;
    const firmDoc = firmPrimary?.docs[0] ?? firmAdditional?.docs[0] ?? null;
    if (firmDoc) {
      resolvedFirmAdmin = {
        firmId: firmDoc.id,
        firm: normalizeFirm(firmDoc.id, firmDoc.data()),
        isPrimaryAdmin: !!(firmPrimary && !firmPrimary.empty),
      };
    }

    // Resolve coach — by uid first, email fallback on first login before uid is stamped
    let resolvedCoach: CoachInfo | null = null;
    const coachByUid = coachByUidResult.status === 'fulfilled' ? coachByUidResult.value : null;
    let coachDoc = coachByUid?.docs[0] ?? null;

    if (!coachDoc) {
      try {
        const byEmail = await getDocs(query(
          collection(db, 'coaches'),
          where('email', '==', firebaseUser.email),
          where('status', '==', 'active'),
        ));
        coachDoc = byEmail.docs[0] ?? null;
        if (coachDoc) {
          // Stamp uid so future logins resolve by uid without the email fallback.
          // Awaited so the page never loads before the stamp lands — isActiveCoach
          // would otherwise see uid==null and deny all profile reads.
          try {
            await updateDoc(doc(db, 'coaches', coachDoc.id), { uid: firebaseUser.uid });
          } catch {
            // Non-fatal: isActiveCoach email fallback covers this session if stamp fails.
          }
        }
      } catch {}
    }

    if (coachDoc) {
      resolvedCoach = {
        coachId: coachDoc.id,
        coach: normalizeCoach(coachDoc.id, coachDoc.data()),
      };
    }

    if (resolvedFirmAdmin !== null || resolvedCoach !== null) {
      setFirmAdmin(resolvedFirmAdmin);
      setCoach(resolvedCoach);
      setUser(null);
      setCollegeAdmin(null);
      setAccessDenied(false);
      return;
    }

    // 4. College admin — only checked when no firm/coach match
    try {
      const [primarySnap, additionalSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'collegeAccounts'),
          where('contactEmail', '==', firebaseUser.email),
          where('active', '==', true),
        )),
        getDocs(query(
          collection(db, 'collegeAccounts'),
          where('adminEmails', 'array-contains', firebaseUser.email),
          where('active', '==', true),
        )),
      ]);
      const accountDoc = primarySnap.docs[0] ?? additionalSnap.docs[0];
      if (accountDoc) {
        setCollegeAdmin({
          accountId: accountDoc.id,
          account: normalizeAccount(accountDoc.id, accountDoc.data()),
          isPrimaryAdmin: !primarySnap.empty,
        });
        setUser(null);
        setFirmAdmin(null);
        setCoach(null);
        setAccessDenied(false);
        return;
      }
    } catch {}
  }

  setAccessDenied(true);
  setUser(null);
  setCollegeAdmin(null);
  setFirmAdmin(null);
  setCoach(null);
  await fbSignOut(auth);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Captured synchronously at render time — BEFORE React Router's useLayoutEffect
  // fires and rewrites window.location via <Navigate to="/dashboard">.
  // By the time useEffect runs, the URL is already /dashboard and query params are gone.
  const initialHref = useRef(window.location.href);

  const [user, setUser] = useState<User | null>(null);
  const [collegeAdmin, setCollegeAdmin] = useState<CollegeAdminInfo | null>(null);
  const [firmAdmin, setFirmAdmin] = useState<FirmAdminInfo | null>(null);
  const [coach, setCoach] = useState<CoachInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [pendingMagicLink, setPendingMagicLink] = useState(false);
  const [magicLinkInProgress, setMagicLinkInProgress] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');

  const pendingLinkUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const init = async () => {
      const capturedUrl = initialHref.current;
      const isLink = hasMagicLinkParams(capturedUrl);

      if (isLink) {
        setMagicLinkInProgress(true);

        const storedEmail = window.localStorage.getItem(MAGIC_EMAIL_KEY);

        if (storedEmail) {
          try {
            await signInWithEmailLink(auth, storedEmail, capturedUrl);
            window.localStorage.removeItem(MAGIC_EMAIL_KEY);
            setMagicLinkInProgress(false);
          } catch (err: any) {
            setMagicLinkError(
              `Sign-in failed (${err?.code ?? 'unknown'}). The link may have expired or already been used. Please request a new one.`
            );
            setMagicLinkInProgress(false);
          }
        } else {
          // Email not in localStorage — opened in a different browser.
          // Save the URL for completeMagicLink to use.
          pendingLinkUrlRef.current = capturedUrl;
          setPendingMagicLink(true);
          setMagicLinkInProgress(false);
        }
      }

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          await resolveUser(firebaseUser, setUser, setCollegeAdmin, setFirmAdmin, setCoach, setAccessDenied);
        } else {
          setUser(null);
          setCollegeAdmin(null);
          setFirmAdmin(null);
          setCoach(null);
        }
        setLoading(false);
      });
    };

    init();
    return () => { unsubscribe?.(); };
  }, []);

  const signInWithGoogle = async () => {
    setAccessDenied(false);
    await signInWithPopup(auth, googleProvider);
  };

  const sendMagicLink = async (email: string) => {
    if (!isValidEmail(email)) throw new Error('auth/invalid-email');
    // Store before the call so it is present even if the page reloads mid-flight.
    window.localStorage.setItem(MAGIC_EMAIL_KEY, email);
    try {
      await sendSignInLinkToEmail(auth, email, ACTION_CODE_SETTINGS);
    } catch (err) {
      window.localStorage.removeItem(MAGIC_EMAIL_KEY);
      throw err;
    }
  };

  const completeMagicLink = async (email: string) => {
    const linkUrl = pendingLinkUrlRef.current;
    if (!linkUrl) {
      setMagicLinkError('Sign-in link not found. Please request a new one.');
      return;
    }
    if (!isValidEmail(email)) {
      setMagicLinkError('Please enter a valid email address.');
      return;
    }
    setMagicLinkError('');
    setMagicLinkInProgress(true);
    try {
      await signInWithEmailLink(auth, email, linkUrl);
      pendingLinkUrlRef.current = null;
      setPendingMagicLink(false);
    } catch (err: any) {
      setMagicLinkError(
        `Sign-in failed (${err?.code ?? 'unknown'}). The link may have expired or the email did not match. Please request a new one.`
      );
    } finally {
      setMagicLinkInProgress(false);
    }
  };

  const signOut = async () => {
    await fbSignOut(auth);
    setUser(null);
    setCollegeAdmin(null);
    setFirmAdmin(null);
    setCoach(null);
    setAccessDenied(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        collegeAdmin,
        firmAdmin,
        coach,
        loading,
        accessDenied,
        pendingMagicLink,
        magicLinkInProgress,
        magicLinkError,
        signInWithGoogle,
        sendMagicLink,
        completeMagicLink,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
